class CartProgressManager {
  constructor() {
    this.isProcessing = false;
    this.conversionRate = Shopify?.currency?.rate || 1;
    this.moneyFormat = window.theme?.settings?.money_format || '${{amount}}';
    this.init();
  }

  init() {
    this.updateAllProgressBars();
    this.observeCartChanges();
  }

  observeCartChanges() {
    const cartObserverConfig = { childList: true, subtree: true };
    const cartObserver = new MutationObserver(() => {
      if (!this.isProcessing) {
        this.updateAllProgressBars();
      }
    });

    const cartBody = document.querySelector('#CartDrawer-Body');
    if (cartBody) {
      cartObserver.observe(cartBody, cartObserverConfig);
    }
  }

  async updateAllProgressBars() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      await Promise.all([
        this.updateFreeShippingBar(cart),
        this.updateFreeGiftBar(cart)
      ]);
    } catch (error) {
      console.error('Error updating progress bars:', error);
    }
  }

  calculateEligibleTotal(cart) {
    return cart.items.reduce((total, item) => {
      if (!item.properties?._free_gift) {
        return total + item.final_line_price;
      }
      return total;
    }, 0);
  }

  async updateFreeShippingBar(cart) {
    const shippingBar = document.querySelector('shipping-bar');
    if (!shippingBar) return;

    const threshold = parseInt(shippingBar.dataset.shippingThreshold);
    const progressText = shippingBar.querySelector('.progress-bar__text');
    const progressLine = shippingBar.querySelector('[data-progress-line]');
    const messageElement = shippingBar.querySelector('[data-free-shipping-message]');
    const successElement = shippingBar.querySelector('[data-free-shipping-success]');
    
    if (!threshold || !progressText || !progressLine) return;

    const visibility = cart.item_count === 0 ? 'hidden' : 'visible';
    shippingBar.style.visibility = visibility;

    const eligibleTotal = this.calculateEligibleTotal(cart);
    const remainingAmount = threshold - eligibleTotal;
    const progressPercentage = Math.min((eligibleTotal / threshold) * 100, 100);

    if (eligibleTotal >= threshold) {
      const successMessage = successElement?.textContent || 'You qualify for free shipping!';
      progressText.innerHTML = successMessage;
      progressLine.style.width = '100%';
    } else {
      const messageTemplate = messageElement?.textContent || 'Spend [amount] more for free shipping!';
      const formattedAmount = this.formatMoney(remainingAmount);
      progressText.innerHTML = messageTemplate.replace('[amount]', `<span class="money">${formattedAmount}</span>`);
      progressLine.style.width = `${progressPercentage}%`;
    }
  }

  async updateFreeGiftBar(cart) {
    const giftBar = document.querySelector('free-gift-bar');
    if (!giftBar) return;

    const threshold = parseInt(giftBar.dataset.giftThreshold);
    const variantId = giftBar.dataset.variantId;
    const progressText = giftBar.querySelector('.progress-bar__text');
    const progressLine = giftBar.querySelector('[data-progress-line]');
    const progressMessage = giftBar.dataset.progressMessage;
    const successMessage = giftBar.dataset.successMessage;

    if (!threshold || !variantId || !progressText || !progressLine) return;

    const visibility = cart.item_count === 0 ? 'hidden' : 'visible';
    giftBar.style.visibility = visibility;

    const eligibleTotal = this.calculateEligibleTotal(cart);
    const remainingAmount = threshold - eligibleTotal;
    const progressPercentage = Math.min((eligibleTotal / threshold) * 100, 100);
    
    const hasGift = cart.items.some(item => 
      String(item.variant_id) === String(variantId) && item.properties?._free_gift
    );

    if (eligibleTotal >= threshold) {
      progressText.innerHTML = successMessage;
      progressLine.style.width = '100%';
      
      if (!hasGift && !this.isProcessing) {
        await this.addFreeGift(variantId);
      }
    } else {
      const formattedAmount = this.formatMoney(remainingAmount);
      progressText.innerHTML = progressMessage.replace('[amount]', `<span class="money">${formattedAmount}</span>`);
      progressLine.style.width = `${progressPercentage}%`;
      
      if (hasGift && !this.isProcessing) {
        await this.removeFreeGift(cart, variantId);
      }
    }
  }

  async addFreeGift(variantId) {
    if (this.isProcessing) return;
    this.isProcessing = true;

    try {
      this.showOverlay('Adding your free gift...');

      const formData = {
        items: [{
          id: variantId,
          quantity: 1,
          properties: {
            _free_gift: 'true'
          }
        }]
      };

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        await this.refreshCart();
      } else {
        console.error('Failed to add free gift');
      }
    } catch (error) {
      console.error('Error adding free gift:', error);
    } finally {
      this.hideOverlay();
      this.isProcessing = false;
    }
  }

  async removeFreeGift(cart, variantId) {
    if (this.isProcessing) return;
    this.isProcessing = true;

    try {
      this.showOverlay('Updating cart...');

      const giftItem = cart.items.find(item => 
        String(item.variant_id) === String(variantId) && item.properties?._free_gift
      );

      if (giftItem) {
        const updates = {};
        updates[giftItem.key] = 0;

        const response = await fetch('/cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ updates })
        });

        if (response.ok) {
          await this.refreshCart();
        } else {
          console.error('Failed to remove free gift');
        }
      }
    } catch (error) {
      console.error('Error removing free gift:', error);
    } finally {
      this.hideOverlay();
      this.isProcessing = false;
    }
  }

  async refreshCart() {
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer) return;

    try {
      const response = await fetch(window.location.pathname, {
        method: 'GET',
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      const sections = [
        { selector: '#CartDrawer-Body', id: 'CartDrawer-Body' },
        { selector: '#cart-counter', id: 'shopify-section-cart-counter' }
      ];

      sections.forEach(({ selector, id }) => {
        const newContent = doc.querySelector(`#${id}`);
        const currentContent = document.querySelector(selector);
        
        if (newContent && currentContent) {
          if (selector === '#CartDrawer-Body') {
            currentContent.innerHTML = newContent.innerHTML;
          } else {
            const currentSection = document.querySelector(`#${id}`);
            if (currentSection) {
              currentSection.innerHTML = newContent.innerHTML;
            }
          }
        }
      });

      if (typeof updateCartCounters === 'function') {
        updateCartCounters();
      }
    } catch (error) {
      console.error('Error refreshing cart:', error);
      window.location.reload();
    }
  }

  showOverlay(message = 'Processing...') {
    let overlay = document.querySelector('.cart-progress-overlay');
    
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.className = 'cart-progress-overlay';
      overlay.innerHTML = `
        <div class="cart-progress-overlay__content">
          <div class="cart-progress-overlay__spinner"></div>
          <p class="cart-progress-overlay__message">${message}</p>
        </div>
      `;
      
      const cartDrawerBody = document.querySelector('#CartDrawer-Body');
      if (cartDrawerBody) {
        cartDrawerBody.style.position = 'relative';
        cartDrawerBody.appendChild(overlay);
      }
    } else {
      overlay.querySelector('.cart-progress-overlay__message').textContent = message;
    }

    requestAnimationFrame(() => {
      overlay.classList.add('is-visible');
    });
  }

  hideOverlay() {
    const overlay = document.querySelector('.cart-progress-overlay');
    if (overlay) {
      overlay.classList.remove('is-visible');
      setTimeout(() => overlay.remove(), 300);
    }
  }

  formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    return this.moneyFormat
      .replace('{{amount}}', amount)
      .replace('{{amount_no_decimals}}', Math.round(cents / 100))
      .replace('{{amount_with_comma_separator}}', amount.replace('.', ','));
  }
}

if (typeof updateFreeShipping === 'function') {
  const originalUpdateFreeShipping = updateFreeShipping;
  window.updateFreeShipping = function() {
    if (window.cartProgressManager) {
      window.cartProgressManager.updateAllProgressBars();
    } else {
      originalUpdateFreeShipping();
    }
  };
}

document.addEventListener('DOMContentLoaded', () => {
  window.cartProgressManager = new CartProgressManager();
});

